import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import TaskBoard from '../components/planwise/TaskBoard';
import BoardSelector from '../components/planwise/BoardSelector';
import TeamMemberSelector from '../components/planwise/TeamMemberSelector';
import CreateBoardModal from '../components/planwise/CreateBoardModal';
import AuditDashboard from '../components/planwise/AuditDashboard';
import ShareBoardModal from '../components/planwise/ShareBoardModal';
import {
  FiPlus,
  FiList,
  FiCalendar,
  FiUsers,
  FiShare2,
  FiEye,
  FiLock,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiShield
} from 'react-icons/fi';

const Planwise = () => {
  const { user } = useAuth();
  
  // State management
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'timeline'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showTeamMemberModal, setShowTeamMemberModal] = useState(false);
  const [showAuditDashboard, setShowAuditDashboard] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if user is team admin
  const isTeamAdmin = user?.role === 'team_admin' || user?.role === 'super_admin';

  // Fetch initial data
  useEffect(() => {
    fetchBoards();
    if (isTeamAdmin) {
      fetchTeamMembers();
    }
  }, [isTeamAdmin]);

  // Close team member modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTeamMemberModal && !event.target.closest('.team-member-dropdown')) {
        setShowTeamMemberModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTeamMemberModal]);

  const fetchBoards = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/planwise/boards');
      setBoards(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching boards:', error);
      
      let errorMessage = 'Failed to load boards';
      
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to access boards. Please contact your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page to continue.';
            break;
          case 500:
            errorMessage = 'Server error occurred while loading boards. Please try again later.';
            break;
          default:
            errorMessage = `Failed to load boards: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Find current user in team members list for default selection
  const currentUserInTeam = teamMembers.find(member => member.id === user?.id);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('/api/planwise/team-members');
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
      
      // Only show error if it's a permission issue
      if (error.response?.status === 403) {
        setError('You do not have permission to view team members. Some features may be limited.');
      }
      // Don't set error state for other cases as team members is not critical for basic functionality
    }
  };

  const fetchTeamMemberBoards = async (memberId) => {
    try {
      const response = await axios.get(`/api/planwise/team-members/${memberId}/boards`);
      setBoards(response.data);
      setSelectedBoard(response.data[0] || null);
    } catch (error) {
      console.error('Error fetching team member boards:', error);
      setError('Failed to load team member boards');
    }
  };

  const handleTeamMemberSelect = (member) => {
    setSelectedTeamMember(member);
    if (member) {
      fetchTeamMemberBoards(member.id);
    } else {
      // Reset to own boards
      setSelectedTeamMember(null);
      fetchBoards();
    }
  };

  const handleCreateBoard = async (boardData) => {
    try {
      const response = await axios.post('/api/planwise/boards', boardData);
      const newBoard = response.data;
      
      // Add new board to state
      setBoards([...boards, newBoard]);
      
      // Select the new board
      setSelectedBoard(newBoard);
      
      // Clear any errors
      setError(null);
      
      return newBoard;
    } catch (error) {
      console.error('Error creating board:', error);
      
      let errorMessage = 'Failed to create board';
      
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to create boards. Please contact your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 400:
            errorMessage = `Invalid board data: ${error.response.data?.message || 'Please check your input and try again'}`;
            break;
          case 409:
            errorMessage = 'A board with this name already exists. Please choose a different name.';
            break;
          case 500:
            errorMessage = 'Server error occurred while creating the board. Please try again later.';
            break;
          default:
            errorMessage = `Failed to create board: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleBoardSelect = (board) => {
    setSelectedBoard(board);
  };

  const handleBoardDelete = async (boardId) => {
    try {
      await axios.delete(`/api/planwise/boards/${boardId}`);
      
      // Remove board from state
      setBoards(boards.filter(board => board.id !== boardId));
      
      // If deleted board was selected, clear selection
      if (selectedBoard?.id === boardId) {
        setSelectedBoard(null);
      }
      
      // Show success message
      setError(null);
    } catch (error) {
      console.error('Error deleting board:', error);
      
      let errorMessage = 'Failed to delete board';
      
      if (error.response) {
        switch (error.response.status) {
          case 404:
            errorMessage = 'You do not have permission to delete this board, or it may have already been deleted. Only board owners and team admins can delete boards.';
            break;
          case 403:
            errorMessage = 'You do not have permission to delete this board. Only board owners and team admins can delete boards.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 409:
            errorMessage = 'Cannot delete board as it contains tasks. Please delete all tasks first or contact your team admin.';
            break;
          case 500:
            errorMessage = 'Server error occurred while deleting the board. Please try again later.';
            break;
          default:
            errorMessage = `Failed to delete board: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (selectedBoard) {
      // Update board view mode
      axios.put(`/api/planwise/boards/${selectedBoard.id}`, {
        view_mode: mode
      }).catch(console.error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="h-72 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="lg:col-span-3 h-72 bg-gray-300 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/30">
      <div className="w-full p-4">
        {/* Header */}
        <div className="mb-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center">
                  <FiList className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    Planwise
                  </h1>
                  {selectedTeamMember && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Managing tasks for {selectedTeamMember.firstName} {selectedTeamMember.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Team Member Selector (Team Admin Only) */}
              {isTeamAdmin && (
                <div className="relative team-member-dropdown">
                  <button
                    onClick={() => setShowTeamMemberModal(!showTeamMemberModal)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                  >
                    <FiUsers className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {selectedTeamMember
                        ? `${selectedTeamMember.firstName} ${selectedTeamMember.lastName}`
                        : 'Team Members'
                      }
                    </span>
                    <FiChevronDown className="w-3 h-3 text-gray-500" />
                  </button>

                  {showTeamMemberModal && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg z-50 overflow-hidden">
                      <div className="p-2">
                        <TeamMemberSelector
                          members={teamMembers}
                          selectedMember={selectedTeamMember}
                          onMemberSelect={(member) => {
                            handleTeamMemberSelect(member);
                            setShowTeamMemberModal(false);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* View Mode Selector */}
              <div className="flex bg-white dark:bg-gray-800 rounded-md p-1 border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleViewModeChange('list')}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="List View"
                >
                  <FiList className="w-3.5 h-3.5" />
                  Board
                </button>
                <button
                  onClick={() => handleViewModeChange('timeline')}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'timeline'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="Timeline View"
                >
                  <FiCalendar className="w-3.5 h-3.5" />
                  Timeline
                </button>
              </div>

              {/* Audit Dashboard Button (Team Admin Only) */}
              {isTeamAdmin && (
                <button
                  onClick={() => setShowAuditDashboard(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
                  title="View deletion audit logs and accountability"
                >
                  <FiShield className="w-4 h-4 text-amber-600 dark:text-amber-500" />
                  Audit
                </button>
              )}

              {/* Create Board Button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                <FiPlus className="w-4 h-4" />
                New Board
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-md">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5 bg-red-500 rounded-full text-white font-bold text-xs flex-shrink-0">!</span>
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Main Content Layout */}
        <div className="flex gap-4">
          {/* Sidebar */}
          <div className={`transition-all duration-200 ${
            sidebarCollapsed ? 'w-12' : 'w-64'
          } flex-shrink-0`}>
            <div className="space-y-4">
              {/* Sidebar Toggle */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="w-full flex items-center justify-center p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              >
                {sidebarCollapsed ? (
                  <FiChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                ) : (
                  <FiChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>

              {!sidebarCollapsed && (
                <>
                  {/* Board Selector */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Task Boards
                      </h3>
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300">
                        {boards.length}
                      </span>
                    </div>
                    <BoardSelector
                      boards={boards}
                      selectedBoard={selectedBoard}
                      onBoardSelect={handleBoardSelect}
                      onBoardDelete={handleBoardDelete}
                      canEdit={!selectedTeamMember || selectedTeamMember.id === user?.id}
                    />
                  </div>

                  {/* Board Info */}
                  {selectedBoard && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: selectedBoard.color }}
                        />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {selectedBoard.name}
                        </h3>
                      </div>

                      {selectedBoard.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                          {selectedBoard.description}
                        </p>
                      )}

                      <div className="flex items-center gap-2 mb-3">
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          selectedBoard.is_private
                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                            : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                        }`}>
                          {selectedBoard.is_private ? (
                            <>
                              <FiLock className="w-3 h-3" />
                              Private
                            </>
                          ) : (
                            <>
                              <FiEye className="w-3 h-3" />
                              Shared
                            </>
                          )}
                        </div>
                      </div>

                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Owner</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {selectedBoard.owner?.firstName} {selectedBoard.owner?.lastName}
                          </span>
                        </div>
                        {/* Share button — only shown to the board owner (admin
                            collaborators can share too, but the server enforces
                            that; we just hide from non-owners to avoid a 403). */}
                        {selectedBoard.owner_id === user?.id && (
                          <button
                            type="button"
                            onClick={() => setShowShareModal(true)}
                            className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                          >
                            <FiShare2 className="w-3.5 h-3.5" />
                            Share Board
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {selectedBoard ? (
              <TaskBoard
                board={selectedBoard}
                viewMode={viewMode}
                canEdit={!selectedTeamMember || isTeamAdmin}
                onBoardUpdate={fetchBoards}
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-10 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center mx-auto mb-3">
                    <FiList className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No Board Selected</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">
                    {boards.length === 0
                      ? 'Create your first task board to start organising your projects and tasks'
                      : 'Select a task board from the sidebar to view and manage your tasks'
                    }
                  </p>
                  {boards.length === 0 && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors mx-auto"
                    >
                      <FiPlus className="w-4 h-4" />
                      Create Your First Board
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Board Modal */}
        {showCreateModal && (
          <CreateBoardModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateBoard}
          />
        )}

        {/* Audit Dashboard Modal (Team Admin Only) */}
        {showAuditDashboard && isTeamAdmin && (
          <AuditDashboard
            onClose={() => setShowAuditDashboard(false)}
          />
        )}

        {/* Share Board Modal */}
        {showShareModal && selectedBoard && (
          <ShareBoardModal
            boardId={selectedBoard.id}
            onClose={() => setShowShareModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Planwise;
